import clsx from 'clsx';
import { get, isString, isNil } from 'lodash';
import { i18n } from '@lingui/core';

import format, { FORMATTER_TYPES } from '../../utilities/FormattingUtilities';
import { INNER_CELL_VALUE_CLASS, ROWWIDTH } from './DataTableConstants';
import {
    linkColumnRenderer,
    drillInLinkColumnRenderer,
    testLogLinkColumnRenderer,
    problemLinkColumnRenderer,
    iconLinkColumnRenderer,
} from './columns/Link';
import { statusColumnRenderer, statusColumnFormatter } from './columns/Status';
import { statusTrendColumnRenderer } from './columns/StatusTrend';
import { iconRenderer } from './columns/Icon';
import { barChartRenderer } from './columns/BarChart';
import { selectionHeaderRenderer, selectionRenderer } from './columns/Selection';
import { sparklinkColumnRenderer } from './columns/SparklineColumn';

export const COLUMN_TYPES = {
    TEXT: 'text',
    LINK: 'link',
    PROBLEMLINK: 'problemLink',
    TESTLOGLINK: 'testlogLink',
    DRILLINLINK: 'drillinLink',
    NUMERIC: 'numeric',
    ICON: 'icon',
    ICONLINK: 'iconLink',
    STATUS: 'status',
    STATUSTREND: 'statusTrend',
    BARCHART: 'barchart',
    SELECTION: 'selection',
    SPARKLINE: 'sparkline',
    FLEX: 'flex', // internal use only, please avoid this one. Use the `flex` property instead.
};

export const DEFAULT_COLUMN_CONFIG = {
    type: COLUMN_TYPES.TEXT,
};

const COLUMN_DEFAULTS = {
    formatter: FORMATTER_TYPES.TEXT,
    align: 'left',
    sortable: false,
    width: ROWWIDTH.MIN,
    minWidth: ROWWIDTH.MIN, // note - anything under 16 will cause wrapping potentially and 0 will be removed from the visible columns
    maxWidth: ROWWIDTH.MAX,
    flex: null,
    headerGroup: null,

    headerClassName: null,
    className: null,

    csvOnly: false, // column is only visible when generating a CSV
    uiOnly: false, // column is only visible in the UI. It will not appear in CSV or PDF reports.

    headerTooltip: null,
    getHeaderTooltip: defaultHeaderTooltipGetter,
    tooltip: true,
    getTooltip: defaultTooltipGetter,

    mapper: defaultColumnMapper,
    renderer: defaultColumnRenderer,
    headerRenderer: defaultColumnHeaderRenderer,
};

export const COLUMN_TYPE_DEFAULTS = {
    [COLUMN_TYPES.TEXT]: {
        ...COLUMN_DEFAULTS,
        className: 'display-inline',
    },
    [COLUMN_TYPES.LINK]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.LINK,
        renderer: linkColumnRenderer,
        removable: false,
    },
    [COLUMN_TYPES.DRILLINLINK]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.LINK,
        renderer: drillInLinkColumnRenderer,
        removable: false,
    },
    [COLUMN_TYPES.TESTLOGLINK]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.TESTLOGLINK,
        getTooltip: () => 'View Test Log',
        renderer: testLogLinkColumnRenderer,
        removable: false,
    },
    [COLUMN_TYPES.PROBLEMLINK]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.PROBLEMLINK,
        formatter: (row) => row.entity?.name,
        getTooltip: (row) => row.entity?.name,
        renderer: problemLinkColumnRenderer,
        removable: false,
    },
    [COLUMN_TYPES.NUMERIC]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.NUMERIC,
        align: 'right',
        formatter: FORMATTER_TYPES.DECIMAL,
    },
    [COLUMN_TYPES.ICON]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.ICON,
        align: 'center',
        tooltip: false,
        renderer: iconRenderer,
        resizable: false,
    },
    [COLUMN_TYPES.ICONLINK]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.ICONLINK,
        align: 'center',
        tooltip: false,
        renderer: iconLinkColumnRenderer,
        removable: false,
    },
    [COLUMN_TYPES.STATUS]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.STATUS,
        align: 'center',
        width: null,
        tooltip: false,
        renderer: statusColumnRenderer,
        csvFormatter: statusColumnFormatter,
    },
    [COLUMN_TYPES.STATUSTREND]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.STATUSTREND,
        align: 'left',
        flex: 1,
        resizable: false,
        minWidth: ROWWIDTH.STATUS_TREND_MIN,
        width: null,
        tooltip: false,
        renderer: statusTrendColumnRenderer,
    },
    [COLUMN_TYPES.BARCHART]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.BARCHART,
        align: 'left',
        tooltip: false,
        renderer: barChartRenderer,
    },
    [COLUMN_TYPES.SPARKLINE]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.SPARKLINE,
        align: 'left',
        tooltip: false,
        renderer: sparklinkColumnRenderer,
    },
    [COLUMN_TYPES.SELECTION]: {
        ...COLUMN_DEFAULTS,
        width: 45,
        id: 'selectionColumn',
        sortable: false,
        tooltip: false,
        headerRenderer: selectionHeaderRenderer,
        renderer: selectionRenderer,
        resizable: false,
        uiOnly: true,
    },
    [COLUMN_TYPES.FLEX]: {
        ...COLUMN_DEFAULTS,
        type: COLUMN_TYPES.FLEX,
        flex: 1,
        minWidth: 0,
        id: 'flexColumn',
        headerClassName: 'disable-padding',
        className: 'disable-padding',
        formatter: null,
        mapper: null,
        tooltip: false,
        resizable: false,
        renderer: () => '',
        headerRenderer: () => '',
    },
};

export function defaultColumnMapper(row, column) {
    const { mapping } = column;

    return get(row, mapping);
}

export function columnValueGetter(row, column) {
    const { mapper, formatter } = column;
    let value = mapper(row, column);

    if (formatter) {
        if (typeof formatter === 'function') {
            // Do not change this to include the column configuration as a parameter! All formatters should be decoupled from the datatable.
            value = formatter(value, row);
        } else {
            // Do not change this to include the column configuration as a parameter! All formatters should be decoupled from the datatable.
            value = format(value, isString(formatter) ? { type: formatter } : formatter);
        }
    }

    return value;
}

export function defaultColumnRenderer(row, column) {
    const value = columnValueGetter(row, column);

    return <div className={`${clsx(INNER_CELL_VALUE_CLASS, column.className)}`}>{value}</div>;
}

export function defaultColumnHeaderRenderer(column) {
    const { title } = column;

    if (typeof title === 'object') {
        return i18n._(title);
    }

    return title;
}

export function defaultHeaderTooltipGetter(column) {
    return column.headerTooltip;
}

export function defaultTooltipGetter(row, column) {
    let { tooltip } = column;

    if (tooltip === false || isNil(tooltip)) {
        return null;
    }

    if (tooltip === true) {
        tooltip = columnValueGetter(row, column);
    }

    return tooltip;
}
